import { takeLatest, call, put, delay, select, takeEvery } from "redux-saga/effects"
import {
	createEventRequestFailed,
	createEventRequestSuccess,
	deleteEventRequestFailed,
	deleteEventRequestSuccess,
	getEventsRequest,
	getEventsRequestFailed,
	getEventsRequestSuccess,
	setSelectedEvents,
} from "../actions/eventActions"
import { eventServices } from "../services/event.services"
import { DELETE_EVENT_REQUEST, GET_EVENTS_REQUEST, SAVE_EVENT_REQUEST } from "../types"
import moment from "moment"
import { setNotification } from "../actions/notificationActions"
import { addGraphRecord, binarySearch, removeGraphRecord } from "../utils"
import { getGraphDataRequestSuccess } from "../actions/createTimelineActions"
import { CLIENT_SIDE_MESSAGES as Messages } from "../utils"

function convetEventToUTC(event) {
	let utcDate = moment.utc(event?.start_date).format("MM-DD-YYYY") //convert dateto UTC timezome
	let utcEndDate = moment.utc(event?.end_date).format("MM-DD-YYYY") //convert dateto UTC timezome

	const date = new Date(utcDate)
	const endDate = new Date(utcEndDate)

	const utcTimestamp = Date.UTC(
		date.getUTCFullYear(),
		date.getUTCMonth(),
		date.getUTCDate(),
		date.getUTCHours(),
		date.getUTCMinutes(),
		date.getUTCSeconds(),
		date.getUTCMilliseconds()
	)

	const utcEndDateTimestamp = Date.UTC(
		endDate.getUTCFullYear(),
		endDate.getUTCMonth(),
		endDate.getUTCDate(),
		endDate.getUTCHours(),
		endDate.getUTCMinutes(),
		endDate.getUTCSeconds(),
		endDate.getUTCMilliseconds()
	)

	// Adjust for local time zone offset
	const localTimestamp = utcTimestamp - date.getTimezoneOffset() * 60 * 1000
	const localTimestampEndDate = utcEndDateTimestamp - endDate.getTimezoneOffset() * 60 * 1000

	return {
		...event,
		start_date: moment.utc(event?.start_date).format("MM-DD-YYYY"),
		end_date: moment.utc(event?.end_date).format("MM-DD-YYYY"),
		date: localTimestamp,
		endDate: localTimestampEndDate,
	}
}

function* getEventsSaga() {
	try {
		const result = yield call(eventServices.getEvents)
		if (result?.status === 200 && result?.data?.success === true) {
			//format data and handle timezone
			const data = result?.data?.data?.map((event) => convetEventToUTC(event)) || []

			yield put(getEventsRequestSuccess(data))
		} else {
			yield put(getEventsRequestFailed(result?.data?.message))
		}
	} catch (error) {
		yield put(getEventsRequestFailed(Messages.SERVER_ERROR))
	}
}

function* saveEventsSaga(action) {
	try {
		const { id, ...data } = action?.payload
		const result = yield call(eventServices.saveEvent, data, id)
		if (result?.status === 200 && result?.data?.success === true) {
			yield put(createEventRequestSuccess(data))
			yield put(setNotification({ data: result?.data?.message, severity: "success" }))
			if (!id) {
				//new event is created then in event list
				//1. add this new event in selected event - this will select a newly created event
				//2. if start date is not availble in graph data then add a dummy record on graph data
				//3 add new record in event list
				const selectedEvents = yield select((state) => state.events.selectedEvents)
				const graphData = yield select((state) => state.timeline.graph)
				const eventsList = yield select((state) => state.events.list)

				const newEvent = convetEventToUTC(result?.data?.data)
				const graph = addGraphRecord(graphData, newEvent)
				yield put(getGraphDataRequestSuccess(graph)) //update the graph data
				// if (graphData.length !== graph.length) {
				// 	yield put(getGraphDataRequestSuccess(graph)) //update the graph data
				// }

				const newEventsList = _.cloneDeep(eventsList)
				newEventsList.unshift(newEvent)
				yield put(getEventsRequestSuccess(newEventsList))

				const newSelectedEvents = _.cloneDeep(selectedEvents)
				newSelectedEvents.push(newEvent)
				yield put(setSelectedEvents(newSelectedEvents))
			} else {
				//if the selected event is updated
				const selectedEvents = yield select((state) => state.events.selectedEvents) || []
				const updateEventIndex = selectedEvents?.findIndex((event) => event.id === id)
				if (updateEventIndex !== -1) {
					const updatedEvent = convetEventToUTC(result?.data?.data)
					const newSelectedEvents = _.cloneDeep(selectedEvents)
					newSelectedEvents[updateEventIndex] = updatedEvent
					yield put(setSelectedEvents(newSelectedEvents))
				}
			}
		} else {
			yield put(createEventRequestFailed(result?.data?.message))
			yield put(setNotification({ data: result?.data?.message, severity: "error" }))
		}
	} catch (error) {
		yield put(createEventRequestFailed(error))
		yield put(setNotification({ data: error, severity: "error" }))
	}
}

function* deleteEventsSaga(action) {
	try {
		const { id, ...data } = action?.payload
		const result = yield call(eventServices.deleteEvent, id)
		if (result?.status === 200 && result?.data?.success === true) {
			yield put(deleteEventRequestSuccess(data))
			yield put(setNotification({ data: result?.data?.message, severity: "success" }))

			/**
			 * 1. Check the deleted event is present in the selected event or not
			 * 2. Remove that event from selected event
			 * 3. Check that event entry in graph with 0 docs
			 * 4. If found then remove that graph entry and update the graph
			 * 5. Trigger delete API
			 * 6. After success remove event from event list
			 * 7. Re-fetch event list in background
			 */
			const selectedEvents = yield select((state) => state.events.selectedEvents)
			const graphData = yield select((state) => state.timeline.graph)

			const newSelectedEvents = selectedEvents.filter((event) => event.id !== id)

			const index = binarySearch(graphData, data?.date)
			if (index !== -1 && graphData[index][0] === data?.date && graphData[index][1] === 0) {
				//found 0 doc record in graph
				const graph = removeGraphRecord(graphData, data)
				yield put(getGraphDataRequestSuccess(graph)) //update the graph data
				// if (graphData.length !== graph.length) {
				// 	//if we remove event we should also remove the record from graphData we we have entry for 0 docs
				// 	yield put(getGraphDataRequestSuccess(graph)) //update the graph data
				// }
			}
			yield put(setSelectedEvents(newSelectedEvents)) //update selected event list
			yield put(getEventsRequest())
			//yield call(getEventsSaga) //refresh event list
		} else {
			yield put(deleteEventRequestFailed(result?.data?.message))
			yield put(setNotification({ data: result?.data?.message, severity: "error" }))
		}
	} catch (error) {
		yield put(deleteEventRequestFailed(error))
		yield put(setNotification({ data: error, severity: "error" }))
	}
}

export default function* eventSagas() {
	yield takeLatest(GET_EVENTS_REQUEST, getEventsSaga)
	yield takeLatest(SAVE_EVENT_REQUEST, saveEventsSaga)
	yield takeLatest(DELETE_EVENT_REQUEST, deleteEventsSaga)
}
